import React from 'react';
import validator from 'validator';
import { Select } from 'antd';
import store from 'store';
import { ssCommonUtils } from '@bit/ui-common.ui-components/ss-utils';

const
Option = Select.Option,
{ getDataType } = ssCommonUtils;

const
locale = 'en-US',
validationRules = {
    alpha: s => validator.isAlpha(s, locale) ? true
        : 'Only letters allowed.',

    alphanumeric: s => validator.isAlphanumeric(s, locale) ? true
        : 'Only numbers and letters allowed.',

    email: s => validator.isEmail(s) ? true
        : 'Should be in email format.',

    phone: s => validator.matches(s, /^[+]?[0-9\-\(\)x\s]+$/im) ? true
        : 'Must be a phone number.',

    number: s => validator.isInt(s, locale) ? true
        : 'Only numbers allowed.',

    bool: (v, r, m) => v === r ? true
        : m == null ? 'Must be selected.' : m,

    'min-length': (s, m) => s.length >= m ? true
        : `Must have ${m} or more characters.`,

    'min-length-number': (s, m) => s.length >= m ? true
        : `Must have ${m} or more digits.`,

    object: (v, p, m) => {
        let defaultMsg = 'Please complete required data.';
        return p == null
            ? getDataType(v) === 'Object' ? true : defaultMsg
            : getDataType(v) === p ? true : m == null ? defaultMsg : m;
    },

    'file-types': (v, p, m) => {
        let defaultMsg = ' file type not supported';
        if (v != null && getDataType(v) === 'Object') {
          let ext = v.file.name.slice(-4).toLowerCase();
          return m.includes(ext) ? true : ext.concat(defaultMsg);
        }

        if (v != null && p != null && getDataType(v) === p) {
          let ext = v.name.slice(-4).toLowerCase();
          return m.includes(ext) ? true : ext.concat(defaultMsg);
        }
        return defaultMsg;
    },

    regex: (s, e, m) => (new RegExp(e)).test(s) ? true
        : m == null ? 'Invalid input.' : m,

    spaces: s => s === ' ' ? true
        : 'No spaces are allowed.'
}

function removeDuplicates(d) {
    let geo = new Set(d), result = [];
    geo.forEach((g) => { result.push(g); });
    return result;
}

function getSortedGeo(data) {
    let geo = [];
    for(let p in data) geo.push(data[p].description);
    geo.sort();
    geo = geo.map(e => {
        for(let p in data)
            if(data[p].description === e)
                return data[p];
    });
    return removeDuplicates(geo);
}

export function Request() {
    this.status = null;
    this.code = null;
    this.error = null;
    this.msg = null;
}

export function FormField(c) {
    const
    geneicFormProps = {
        validateStatus: '',
        help: '',
        required: false
    },
    genericFieldProps = {
        value: ''
    },
    assignProps = function(t) {
        switch(t) {
            case 'form':
                let formProps = {};
                for(let p in c.form) formProps[p] = c.form[p];
                return formProps;

            case 'field':
                let fieldProps = {};
                for(let p in c.field) fieldProps[p] = c.field[p];
                return fieldProps;

            case 'validation':
                return new Set(c.validation);
        }
    };

    this.form = {...geneicFormProps};
    this.field = { value: '' };
    this.valid = false;
    this.validation = null;
    this.meta = null;
    this.formProp = null;

    // SETUP PROPS //

    if(c.form) this.form = assignProps('form', c.form);
    for(let p in geneicFormProps)
        if(!this.form.hasOwnProperty(p))
            this.form[p] = geneicFormProps[p];

    if(c.field) this.field = assignProps('field', c.field);
    for(let p in genericFieldProps)
        if(!this.field.hasOwnProperty(p))
            this.field[p] = genericFieldProps[p];

    if(c.valid) this.valid = c.valid || false;

    if(c.validation) this.validation = assignProps('validation', c.validation);
    else this.validation = new Set();

    if(c.meta) this.meta = c.meta;
    if(c.formProp) this.formProp = c.formProp;

    // Pass any addition props into the new object that are not checked.
    for(let p in c) {
        switch(p) {
            case 'form':
            case 'field':
            case 'valid':
            case 'validation':
            case 'meta':
            case 'formProp':
                break;
            default:
                this[p] = c[p];
        }
    }
}

export function validate(input, service) {
    let result = {}, inputInvalid = false, errorMsg = null;
    const { field, form, validation } = input;

    validation.forEach((el, i) => {
        for(let r in validationRules) {
            if(el === r) {
                result[r] = validationRules[r](field.value)
            } else {
                if(el.indexOf(':') !== -1) {
                    let args = el.split(':');
                    const run = function() { result[args[0]] = validationRules[args[0]](...arguments); };
                    switch(args[0]) {
                        case 'min-length': return run(...[
                            field.value,
                            parseInt(args[1])
                        ]);
                        case 'min-length-number': return run(...[
                            field.value,
                            parseInt(args[1])
                        ]);
                        case 'object': return run(...[
                            field.value,
                            args[1] || null,
                            args[2] || null
                        ]);
                        case 'file-types': return run(...[
                            field.value,
                            args[1] || null,
                            args[2] || null
                        ]);
                        case 'bool': return run(...[
                            field.value + '',
                            args[1] || null,
                            args[2] || null
                        ]);
                        case 'regex': return run(...[
                            field.value,
                            args[1] || null,
                            args[2] || null
                        ]);
                        case 'allow-special': return run(...[
                            field.value,
                            args[1] || null
                        ])
                    }
                }
            }
        }
    });

    // Make sure all validation checks are 'true'.
    // Used to determine if this field is 'valid' or not.
    for(let p in result)
        if(typeof result[p] === 'string') {
            inputInvalid = true;
            errorMsg = result[p];
            break;
        }

    // Build new object and update:
    //   > valid
    //   > form.help
    //   > form.validateStatus
    if(form.required && inputInvalid) {
        result = new FormField({...input,
            valid: false,
            form: {...input.form,
                help: errorMsg == null || field.value.length === 0
                    ? 'Field is required'
                    : errorMsg,
                validateStatus: 'error'
            }
        });
    } else {
        result = new FormField({...input,
            valid: true,
            form: {...input.form,
                help: '',
                validateStatus: 'success'
            }
        });
    }

    service(result);
}

export function isStepValid(s, f) {
    let result = true;
    for(let p in s.forms) {
        let
        valid = s.forms[p].valid,
        required = s.forms[p].form.required;
        if(p !== f.formProp && required && !valid) {
            result = false;
            break;
        }
    }
    if(result) {
        if(f.valid)
            return true;
    } else return false;
}

export function buildCountrySelection(country, service) {
    let geo = getSortedGeo(this.props.geo.geoDataStore);

    if(country.hasOwnProperty('options'))
        service(new FormField({...country, options: geo.map((e, i) => {
            return (<Option key={i} value={e.code}>{e.description}</Option>);
        })}));
    else
        service(new FormField({...input, options: [] }));
}

export function buildStateSelection(country, state, service) {
    let c, data = this.props.geo.geoDataStore;

    if(getDataType(country) === 'Object') c = country.field.value;
    else if(getDataType(country) === 'String') c = country;

    if(c.length > 0) {
        for(let p in data) {
            if(data[p].description === c) {
                let geo = getSortedGeo(data[p].states);
                if(state.hasOwnProperty('options')) service(
                    new FormField({...state,
                        options: geo.map((e, i) => {
                            return (<Option key={e.code} value={e.description}>{e.description}</Option>);
                        })
                    })
                );
                else service(new FormField({...input, options: [] }));
            }
        }
    }
}

export function ajax(request) {
    var
    resultData = {
        meta: {
            status: null,
            code: null
        },
        type: null,
        payload: null
    },
    xhr = new XMLHttpRequest(),
    // Use the configuration in the request to parse out the
    // data from the response when passing it back.
    dataFormatter = (d) => {
        if(request.hasOwnProperty('headers')) {
            for(var h in request.headers) {
                var headerType = request.headers[h];
                switch(h) {
                    case 'Content-Type':
                        switch(headerType) {
                            case 'application/json':
                                d = JSON.parse(d);
                                break;
                        }
                        break
                }
            }
        }
        return d;
    },
    getContentType = () => {
        var contentType;
        for(var h in request.headers) {
            contentType = request.headers[h];
        }
        return contentType;
    };

    xhr.onreadystatechange = () => {
        const { readyState, status } = xhr;
        if(readyState == XMLHttpRequest.DONE) {
            var
            obj = {},
            success = request.types[1],
            failure = request.types[2];
            switch((status + '').charAt(0)) {
                // General success 2xx series of responses.
                case '2':
                    if(getDataType(success) === 'Object') {
                        store.dispatch(
                            success.payload(
                                success.type,
                                status,
                                xhr.responseText
                            )
                        );
                    } else if(getDataType(success) === 'String') {
                        return {...returnData,
                            meta: {
                                status: 'success',
                                code: status
                            },
                            type: success,
                            payload: dataFormatter(xhr.responseText)
                        };
                    }
                    break;

                // General failure with the 4xx series of responses.
                case '4':
                    if(getDataType(failure) === 'Object') {
                        store.dispatch(
                            failure.payload(
                                failure.type,
                                status,
                                xhr.responseText
                            )
                        );
                    } else if(getDataType(failure) === 'String') {
                        return {...returnData,
                            meta: {
                                status: 'failure',
                                code: status
                            },
                            type: failure,
                            payload: dataFormatter(xhr.responseText)
                        };
                    }
                    break;
            }
        }
    }

    xhr.open(request.method, request.endpoint, true);
    xhr.setRequestHeader('Content-Type', getContentType());

    switch(request.method) {
        case 'GET':
            xhr.send();
            break;
        case 'POST':
            xhr.send(request.body);
            break;
    }

    return {
        type: request.types[0]
    };
}
